import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, Loader2, ShieldCheck, FileText, Zap } from "lucide-react";

import { API } from "../lib/helpers";
import { Button } from "./ui/button";

/**
 * TripwireOffer — Phase 2A of the funnel.
 *
 * Shown to a visitor immediately AFTER they hand over their email
 * (Phase 1B lead capture). This is the exact intent moment: they just
 * proved they want the analysis. Now we offer the paid Deep-Dive for $7.
 *
 * Copy is deliberately transparent:
 *   - "One-time payment. No automatic subscription." appears verbatim.
 *   - 7-day Pro access is described as complimentary, hard-cutoff,
 *     with saved data preserved after expiry.
 *
 * Funnel events:
 *   - `tripwire_viewed`  → fired by the parent when this component mounts
 *                          (already visible = viewed; no observer needed
 *                          because the parent only mounts it AFTER
 *                          scrolling the SaveCard into view).
 *   - `tripwire_checkout_started` → fired SERVER-SIDE inside
 *                          `POST /api/tripwire/checkout` so it cannot be
 *                          spoofed or double-counted by the client.
 *
 * Props:
 *   analysisId  — the anonymous session ID (must have a linked lead)
 *   trackFunnelEvent — from useAnalysisSession; used for `tripwire_viewed`
 */
export default function TripwireOffer({ analysisId, trackFunnelEvent }) {
  const [loading, setLoading] = useState(false);
  const viewedFiredRef = useRef(false);

  // Fire `tripwire_viewed` exactly once when this offer surfaces.
  // Deliberately runs on mount because the parent already gated on the
  // success state of the SaveCard — i.e., the user has actively scrolled
  // and interacted before this component exists in the DOM.
  useEffect(() => {
    if (viewedFiredRef.current) return;
    viewedFiredRef.current = true;
    trackFunnelEvent?.("tripwire_viewed", {
      analysis_id: analysisId,
      product: "investor_deep_dive",
      amount: 7,
    });
  }, [analysisId, trackFunnelEvent]);

  const handlePurchase = async () => {
    if (!analysisId) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tripwire/checkout`, {
        analysis_id: analysisId,
        origin_url: window.location.origin,
      });
      if (data?.already_purchased) {
        toast.success("You've already unlocked this deep-dive — check your email.");
        setLoading(false);
        return;
      }
      if (!data?.url) {
        throw new Error("no_checkout_url");
      }
      // Full-page redirect to Stripe. This intentionally leaves our SPA
      // so the browser tab that returns from /tripwire/success is the
      // authoritative surface — no state leaks between checkouts.
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      const status = err?.response?.status;
      if (status === 400) {
        toast.error("Please save your analysis with an email first.");
      } else if (status === 404) {
        toast.error("Analysis session expired — please re-run.");
      } else {
        toast.error("Could not start checkout — try again in a moment.");
      }
    }
  };

  return (
    <div
      data-testid="tripwire-offer"
      className="mt-4 border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background/40 to-emerald-500/5 rounded-xl p-5"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-500 mb-0.5">
            Investor Deep-Dive
          </div>
          <div className="font-semibold text-foreground text-base">
            Go deeper on this property for <span className="text-amber-500">$7</span>
          </div>
        </div>
      </div>

      <ul className="space-y-2 mb-4 pl-1">
        <li className="flex items-start gap-2 text-sm text-muted-foreground">
          <FileText className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <span>
            <b className="text-foreground">Full deep-dive PDF</b> — 5/10/20-year projections,
            vacancy & CapEx sensitivity, cash-on-cash + break-even.
          </span>
        </li>
        <li className="flex items-start gap-2 text-sm text-muted-foreground">
          <Zap className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <span>
            <b className="text-foreground">7 days of Pro access</b> — run this same depth on
            any property. Your saved analyses stay after Pro ends.
          </span>
        </li>
        <li className="flex items-start gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <span>
            <b className="text-foreground">One-time payment. No automatic subscription.</b>{" "}
            Nothing gets billed after the 7 days end.
          </span>
        </li>
      </ul>

      <Button
        type="button"
        onClick={handlePurchase}
        disabled={loading || !analysisId}
        className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
        data-testid="tripwire-offer-cta"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to secure checkout…
          </>
        ) : (
          "Unlock Deep-Dive for $7 →"
        )}
      </Button>

      <div className="mt-2 text-[11px] text-muted-foreground/80 text-center">
        Secure Stripe checkout · Refundable within 24h if the numbers don't hold up.
      </div>
    </div>
  );
}
